/**
 * FS-825 — what a Clip is called on screen, so two Clips in one home never
 * read the same.
 *
 * `devices.description` is the owner's surname by design (the sync writes
 * it), so every Clip in a home shares it. The per-Clip name is the filter
 * record's `location`, which the server sends as `filter_location` beside the
 * filter type, this Clip's position among the return grilles, and how many
 * grilles and Clips its system has — on each GET /devices row, on the GET
 * /devices/:id `app_context` and on each system-airflow `clips[]` entry. The
 * label is the description with that name after a middle dot:
 *
 *   name set                              Owner · Upstairs Loft
 *   no name, return grille, >1 Clip       Owner · Return grille 1 of 2
 *   no name, whole-house filter, >1 Clip  Owner · Whole-house filter
 *   no name otherwise                     Owner (as before)
 *
 * "of n" is left off when the grille count is not known, and the position
 * with it when the position is not known, as the Device List's Filter
 * location column does. A whole-house (furnace) filter is never called a
 * grille, named or not. A response from an API before FS-825 carries none of
 * the facts, and every label reads as it did before.
 */

import type { Device, DeviceSearchRow } from '../types';

/** The facts a label is worded from. Every one is optional: an older API sends none. */
export interface ClipLabelFacts {
  /** `devices.description`: the owner's name, shared by every Clip in the home. */
  description?: string | null;
  /** What leads when there is no description; the caller's own fallback (the serial in the header). */
  name?: string | null;
  /** The homeowner's name for this Clip's filter spot. */
  filter_location?: string | null;
  /** `airReturns` | `furnace` | null. */
  filter_type?: string | null;
  /** This Clip's 1-based position among the system's return grilles. */
  filter_slot_ordinal?: number | null;
  /** Return grilles recorded on the system; null when two records name one Clip. */
  hvac_grille_count?: number | null;
  /** Clips on the system; null when the slot list was never synced. */
  hvac_clip_count?: number | null;
}

/** The one word for the furnace filter type, everywhere in the console. */
export const WHOLE_HOUSE_FILTER = 'Whole-house filter';

const textOf = (v: string | null | undefined): string | null => {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t === '' ? null : t;
};

/** "Return grille 2 of 3", "Return grille 2" (no count), "Return grille" (no position). */
export const returnGrilleText = (
  ordinal: number | null | undefined,
  count: number | null | undefined,
): string => {
  let out = 'Return grille';
  if (ordinal != null) out += ` ${ordinal}`;
  if (ordinal != null && count != null) out += ` of ${count}`;
  return out;
};

/**
 * What follows the description: the filter's name, else its slot in words
 * when the system has more than one Clip, else null (nothing is appended).
 */
export const clipFilterName = (facts: ClipLabelFacts): string | null => {
  const name = textOf(facts.filter_location);
  if (name) return name;
  const clips = facts.hvac_clip_count;
  if (clips == null || clips <= 1) return null;
  if (facts.filter_type === 'airReturns') {
    return returnGrilleText(facts.filter_slot_ordinal, facts.hvac_grille_count);
  }
  if (facts.filter_type === 'furnace') return WHOLE_HOUSE_FILTER;
  return null;
};

/** The label: "Owner · Upstairs Loft". Empty only when there is nothing at all to say. */
export const clipLabel = (facts: ClipLabelFacts): string => {
  const lead = textOf(facts.description) ?? textOf(facts.name);
  const tail = clipFilterName(facts);
  return [lead, tail].filter((part): part is string => part != null).join(' · ');
};

/**
 * A device's label, falling back to its serial. GET /devices sends the facts
 * on the row and GET /devices/:id inside `app_context`; either is read.
 */
export const deviceClipLabel = (device: Device): string => {
  const ctx = device.app_context;
  return clipLabel({
    description: device.description,
    name: device.name,
    filter_location: device.filter_location ?? ctx?.filter_location,
    filter_type: device.filter_type ?? ctx?.filter_type,
    filter_slot_ordinal: device.filter_slot_ordinal ?? ctx?.filter_slot_ordinal,
    hvac_grille_count: device.hvac_grille_count ?? ctx?.hvac_grille_count,
    hvac_clip_count: device.hvac_clip_count ?? ctx?.hvac_clip_count,
  });
};

/**
 * A Device List search row's label. The row carries the filter name, type and
 * slot (FS-593's columns) but not how many Clips the system has, so a named
 * Clip shows its name and an unnamed one reads as before.
 */
export const searchRowClipLabel = (row: DeviceSearchRow): string =>
  clipLabel({
    description: row.description,
    name: row.name,
    filter_location: row.app_filter_location,
    filter_type: row.app_filter_type,
    filter_slot_ordinal: row.app_filter_slot_ordinal,
    hvac_grille_count: row.app_hvac_grille_count,
    hvac_clip_count: null,
  });
